import React, { useEffect, useState } from "react";
import { MdSettings } from "react-icons/md";
import { Dialog } from "components/dialog";
import { EMBEDDING_MODEL_TYPE_CONFIGS } from "constants/configuration-models";
import { useTranslation } from "react-i18next";

interface CreateEmbeddingModelModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onSubmit: (data: EmbeddingModelData) => void;
  settingsType: "global" | "organization";
}

interface EmbeddingModelData {
  name: string;
  type: string;
  configuration: string;
}

const CreateEmbeddingModelModal: React.FC<CreateEmbeddingModelModalProps> = ({
  isOpen,
  setIsOpen,
  onSubmit,
  settingsType,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<EmbeddingModelData>({
    name: "",
    type: "Azure OpenAI Text Embedding 3 Small",
    configuration: "",
  });

  const titlePrefix =
    settingsType === "global" ? "" : t("settings.organization");

  const modelTypeConfigs = EMBEDDING_MODEL_TYPE_CONFIGS;
  const modelTypes = Object.keys(modelTypeConfigs);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: "",
        type: "Azure OpenAI Text Embedding 3 Small",
        configuration: JSON.stringify(
          modelTypeConfigs["Azure OpenAI Text Embedding 3 Small"],
          null,
          2
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleTypeChange = (newType: string) => {
    setFormData((prev) => ({
      ...prev,
      type: newType,
      configuration: JSON.stringify(modelTypeConfigs[newType], null, 2),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    setIsOpen(false);
  };

  return (
    <Dialog
      header={t("settings.modelModal.createEmbeddingModel", { type: titlePrefix })}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      className="w-full max-w-lg"
    >
      <form
        onSubmit={handleSubmit}
        className="flex max-h-[70vh] flex-col gap-6 p-2"
      >
        <div className="space-y-6">
          {/* Model Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("settings.modelModal.embeddingModelName")}{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder={t("settings.modelModal.enterEmbeddingName")}
              required
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:focus:border-blue-400"
            />
          </div>

          {/* Model Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("settings.modelModal.embeddingModelType")}{" "}
              <span className="text-red-500">*</span>
            </label>
            <div className="relative mt-1">
              <select
                value={formData.type}
                onChange={(e) => handleTypeChange(e.target.value)}
                className="block w-full appearance-none rounded-lg border border-gray-300 px-3 py-2 pr-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:focus:border-blue-400"
              >
                {modelTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Configuration */}
          <div>
            <div className="mb-2 flex items-center gap-2">
              <MdSettings className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("settings.modelModal.embeddingModelConfiguration")}
              </label>
            </div>
            <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
              {t("settings.modelModal.configurationJsonType")}
            </p>
            <textarea
              value={formData.configuration}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  configuration: e.target.value,
                }))
              }
              placeholder={t("settings.modelModal.enterJsonConfiguration")}
              rows={8}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:focus:border-blue-400"
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-3 pt-6">
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
          >
            {t("settings.modelModal.createModel")}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default CreateEmbeddingModelModal;
